import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { COMPANY_KEY } from '../lib/api.js'
import { useAuth } from './AuthContext.jsx'

const CompanyContext = createContext(null)

// Multi-tenant context. The active company drives the `X-Company-Id` header
// (see lib/api.js). Mirrors the legacy CompanyContext API: currentCompanyId,
// switchCompany, getCurrentCompany, hasRole, isAdmin.
export function CompanyProvider({ children }) {
  const { user } = useAuth()
  const companies = useMemo(() => user?.companies || [], [user])

  const [currentCompanyId, setCurrentCompanyId] = useState(() => {
    const stored = localStorage.getItem(COMPANY_KEY)
    return stored ? Number(stored) : null
  })

  // Default to the first available company once the user/companies load.
  useEffect(() => {
    if (!companies.length) return
    const validIds = companies.map((c) => c.id)
    if (!currentCompanyId || !validIds.includes(currentCompanyId)) {
      const next = companies[0].id
      setCurrentCompanyId(next)
      localStorage.setItem(COMPANY_KEY, String(next))
    }
  }, [companies, currentCompanyId])

  const switchCompany = useCallback((companyId) => {
    setCurrentCompanyId(companyId)
    localStorage.setItem(COMPANY_KEY, String(companyId))
    // Full reload so every screen refetches scoped to the new company.
    window.location.assign('/app/dashboard')
  }, [])

  const getCurrentCompany = useCallback(
    () => companies.find((c) => c.id === currentCompanyId) || null,
    [companies, currentCompanyId],
  )

  // Roles come from two places: top-level `user.roles` (global, e.g.
  // super-administrateur) and the active company's `roles` (per-company, e.g.
  // administrateur). Check both so company-admins are recognised too.
  const hasRole = useCallback(
    (roleName) => {
      const globalRoles = (user?.roles || []).map((r) => (typeof r === 'string' ? r : r.role || r.name))
      if (globalRoles.includes(roleName)) return true
      const company = companies.find((c) => c.id === currentCompanyId)
      return (company?.roles || []).includes(roleName)
    },
    [user, companies, currentCompanyId],
  )

  const isAdmin = useCallback(
    () => hasRole('administrateur') || hasRole('super-administrateur'),
    [hasRole],
  )

  const isSuperAdmin = useCallback(() => hasRole('super-administrateur'), [hasRole])

  const value = {
    companies,
    currentCompanyId,
    switchCompany,
    getCurrentCompany,
    hasRole,
    isAdmin,
    isSuperAdmin,
  }
  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
}

export function useCompany() {
  const ctx = useContext(CompanyContext)
  if (!ctx) throw new Error('useCompany must be used within CompanyProvider')
  return ctx
}
